import { api } from './api';

export type PartnerFailureCategory =
  | 'pharmetika_submission'
  | 'nimbus_patient_sync'
  | 'mdi_case_submit';

export const PARTNER_FAILURE_CATEGORY_LABELS: Record<PartnerFailureCategory, string> = {
  pharmetika_submission: 'Pharmetika submission',
  nimbus_patient_sync: 'Nimbus patient sync',
  mdi_case_submit: 'MDI case submission',
};

export const PARTNER_FAILURE_RELATED_ENTITY: Record<
  PartnerFailureCategory,
  'prescription' | 'patient' | 'appointment' | null
> = {
  pharmetika_submission: 'prescription',
  nimbus_patient_sync: 'patient',
  mdi_case_submit: 'patient',
};

// Categories whose retry action is wired up on the backend.
export const PARTNER_FAILURE_RETRYABLE: ReadonlySet<PartnerFailureCategory> = new Set([
  'nimbus_patient_sync',
  'mdi_case_submit',
]);

export interface PartnerFailure {
  id: string;
  clinic_id: string;
  partner_source: string | null;
  category: PartnerFailureCategory | string;
  stage: string | null;
  error_message: string;
  error_payload: Record<string, unknown> | null;
  context: Record<string, unknown> | null;
  prescription_id: string | null;
  patient_id: string | null;
  appointment_id: string | null;
  practitioner_id: string | null;
  resolved: boolean;
  resolved_by_user_id: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
  retry_attempts: number;
  last_retry_at: string | null;
  last_retry_error: string | null;
  created_at: string;
  updated_at: string;
  prescription?: { id: string; status: string; created_at: string } | null;
  patient?: { id: string; first_name: string; last_name: string } | null;
  appointment?: { id: string; start_time: string; status: string } | null;
  practitioner?: { id: string; full_name: string } | null;
  resolved_by?: { id: string; first_name: string; last_name: string; email: string } | null;
}

export interface PartnerFailureListResponse {
  success: boolean;
  data: {
    items: PartnerFailure[];
    pagination: { page: number; limit: number; total: number; total_pages: number };
  };
}

export interface PartnerFailureCountResponse {
  success: boolean;
  data: {
    count: number;
    by_category: Record<string, number>;
  };
}

export interface PartnerFailureResolveResponse {
  success: boolean;
  data: PartnerFailure;
}

export interface PartnerFailureRetryResponse {
  success: boolean;
  data: PartnerFailure;
  error?: { message: string };
  message?: string;
}

export const partnerFailureService = {
  list(
    clinicId: string,
    opts: { page?: number; limit?: number; resolved?: boolean; category?: string } = {},
  ) {
    const params = new URLSearchParams();
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.resolved !== undefined) params.set('resolved', String(opts.resolved));
    if (opts.category) params.set('category', opts.category);
    const qs = params.toString();
    return api.get<PartnerFailureListResponse>(
      `clinics/${clinicId}/partner-failures${qs ? `?${qs}` : ''}`,
    );
  },

  counts(clinicId: string, resolved = false) {
    return api.get<PartnerFailureCountResponse>(
      `clinics/${clinicId}/partner-failures/count?resolved=${resolved}`,
    );
  },

  resolve(clinicId: string, failureId: string, resolution_note?: string) {
    return api.patch<PartnerFailureResolveResponse>(
      `clinics/${clinicId}/partner-failures/${failureId}/resolve`,
      { resolution_note: resolution_note ?? null },
    );
  },

  retry(clinicId: string, failureId: string) {
    return api.post<PartnerFailureRetryResponse>(
      `clinics/${clinicId}/partner-failures/${failureId}/retry`,
    );
  },
};
